
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

import type { AuthData, User, UserState } from '../types';
import { resolveUserDisplayName } from '../utils/entraIdUserMapper';

// User store - No persistence for maximum security
export const useUserStore = create<UserState>()(
  subscribeWithSelector(
    (set, get) => ({
      // Initial state
      user: null,
      isAuthenticated: false,
      // Start in loading mode so ProtectedRoute waits for session bootstrap.
      isLoading: true,
      error: null,

      // Actions
      setUser: (user: User | null) => {
        set({
          user,
          isAuthenticated: !!user
        });
      },

      setAuthData: (authData: AuthData) => set({
        user: {
          ...authData.user,
        },
        isAuthenticated: true
      }),

      updateUser: (updates: Partial<User>) => set((state: UserState) => ({
        user: state.user ? { ...state.user, ...updates } : null
      })),

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      setError: (error: string | null) => {
        set({ error });
      },

      clearError: () => {
        set({ error: null });
      },

      clearUser: () => set({
        user: null,
        isAuthenticated: false
      }),

      login: (authData: AuthData) => set({
        user: {
          ...authData.user,
        },
        isAuthenticated: true
      }),

      logout: () => set({
        user: null,
        isAuthenticated: false
      }),

      get userDisplayName() {
        const state = get();
        return resolveUserDisplayName(state.user, 'User');
      },
    })
  )
);

// Custom hooks for easier usage
export const useUser = () => useUserStore((state: UserState) => state.user);
export const useIsAuthenticated = () => useUserStore((state: UserState) => state.isAuthenticated);
export const useIsLoading = () => useUserStore((state: UserState) => state.isLoading);

